import { FC, PureComponent } from "react";

import { ContentCopy } from "@livechat/design-system-icons";
import { Avatar, Button, Icon, Tooltip } from "@livechat/design-system-react-components";

import { AccordionItem } from "components/accordion-item/AccordionItem";
import LinkStyledButton from "components/button/LinkStyledButton";
import { CopyToClipboard } from "components/copy-to-clipboard-input";
import { Line } from "components/custom-section";
import Flex from "components/layout/Flex";
import Text from "components/layout/Text";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { ICc, IRequester, TicketSilo } from "interfaces/entities/ticket";

import { TicketDetailsCardTitle } from "./TicketDetailsCardTitle";
import {
  ActionLinkContainer,
  SubtitleWrapper,
  TitleWrapper,
  UserContainer,
  UserData,
  Value,
} from "./TicketDetailsRequesterStyles";
import { SectionWrapper } from "./styles";

interface IProps {
  requester: IRequester;
  ccs: ICc[] | null;
  canEditRequester: boolean;
  canEditCcs: boolean;
  onRequesterModalOpen(): void;
  onCcsModalOpen(): void;
  opened: boolean;
  onHeaderClick(): void;
  ticketSilo: TicketSilo;
  onRequesterEmailCopied(): void;
}

const CopyEmailIcon: FC<{
  email: string;
  onRequesterEmailCopied(): void;
}> = ({ email, onRequesterEmailCopied }) => (
  <Flex inline alignItems="center" marginLeft="4px">
    <CopyToClipboard
      value={email}
      onCopiedHandler={() => {
        trackEngagement("Tickets: Requester email copied", EventPlace.Tickets);
        onRequesterEmailCopied();
      }}
    >
      {({ handleCopy }) => (
        <Tooltip
          triggerRenderer={() => (
            <Button size="xcompact" kind="link-light" onClick={handleCopy} icon={<Icon source={ContentCopy} />} />
          )}
          kind="invert"
          placement="top"
        >
          <Text fontSize="15px" lineHeight="22px" minWidth="50px">
            Copy email address
          </Text>
        </Tooltip>
      )}
    </CopyToClipboard>
  </Flex>
);

export class TicketDetailsRequester extends PureComponent<IProps> {
  private handleRequesterChange = (): void => {
    this.props.onRequesterModalOpen();
    trackEngagement("Tickets: Change requester modal opened", EventPlace.Tickets, { from: "Ticket info" });
  };

  private handleCCsChange = (): void => {
    this.props.onCcsModalOpen();
    trackEngagement("Tickets: Change CCs modal opened", EventPlace.Tickets, { from: "Ticket info" });
  };

  render(): JSX.Element {
    const { requester, ccs, canEditRequester, canEditCcs, opened, onHeaderClick, onRequesterEmailCopied } = this.props;
    const { name, email } = requester;
    const isRecentTicket = this.props.ticketSilo === TicketSilo.Tickets;

    return (
      <SectionWrapper borderTop>
        <AccordionItem
          title={<span data-testid="ticket-requester">Requester</span>}
          opened={opened}
          onHeaderClick={onHeaderClick}
        >
          <TitleWrapper>
            <TicketDetailsCardTitle
              testId="requester-details"
              title="Details"
              primaryButton={{ label: "Change", onClick: this.handleRequesterChange, disabled: !canEditRequester }}
            />
          </TitleWrapper>
          <UserContainer data-testid="assigned-requester">
            <Avatar type="text" text={name ?? email} />
            <UserData>
              {name && (
                <>
                  <Value className="fs-mask">{name}</Value>
                  <Flex alignItems="center">
                    <Value faded>{email}</Value>
                    <CopyEmailIcon email={email} onRequesterEmailCopied={onRequesterEmailCopied} />
                  </Flex>
                </>
              )}
              {!name && (
                <Value>
                  {email}
                  <CopyEmailIcon email={email} onRequesterEmailCopied={onRequesterEmailCopied} />
                </Value>
              )}
            </UserData>
          </UserContainer>
          <Line />
          {ccs && (
            <>
              <SubtitleWrapper>
                <TicketDetailsCardTitle
                  testId="ticket-people-loop"
                  title="People in the loop"
                  primaryButton={{
                    label: "Edit",
                    onClick: this.handleCCsChange,
                    disabled: !canEditCcs || !isRecentTicket,
                  }}
                />
              </SubtitleWrapper>
              {ccs.map((cc, i) => (
                <UserContainer key={cc.email} data-testid={`ticket-cc-person-${i}`}>
                  <Avatar type="text" text={cc.email} />
                  <UserData>
                    <Value className="fs-mask">{cc.email}</Value>
                  </UserData>
                </UserContainer>
              ))}
            </>
          )}
          {!ccs && (
            <ActionLinkContainer>
              <Text inline fontSize="14px">
                <LinkStyledButton
                  onClick={this.handleCCsChange}
                  disabled={!canEditCcs || !isRecentTicket}
                  type="button"
                  data-testid="ticket-details-add-more-people-button"
                >
                  + Add more people
                </LinkStyledButton>
              </Text>
            </ActionLinkContainer>
          )}
        </AccordionItem>
      </SectionWrapper>
    );
  }
}
